import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { type ReactNode } from 'react';
import {
  ArrowLeft, Share2, Bookmark, Clock, Calendar, User, ArrowRight,
  CheckCircle, AlertTriangle, Star, ChevronRight, ExternalLink
} from 'lucide-react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleList, categories } from '@/mocks/data';

const articleContent: Record<string, {
  title: string;
  date: string;
  readTime: string;
  category: string;
  author: string;
  updated: string;
  content: ReactNode;
}> = {
  'allchinabuy-spreadsheet-complete-guide': {
    title: 'AllChinaBuy Spreadsheet Complete Guide: How to Find Products, Links & Buying Resources in 2026',
    date: '2026-08-25',
    readTime: '8 min read',
    category: 'Guide',
    author: 'Editorial Team',
    updated: '2026-08-25',
    content: <AllChinaBuySpreadsheetGuide />,
  },
  'allchinabuy-review-2026': {
    title: 'AllChinaBuy Review 2026: Shipping Costs, QC Process & Platform Features',
    date: '2026-08-20',
    readTime: '6 min read',
    category: 'Review',
    author: 'Editorial Team',
    updated: '2026-08-20',
    content: <AllChinaBuyReview />,
  },
  'allchinabuy-shipping-cost-guide': {
    title: 'AllChinaBuy Shipping Cost Guide: Methods, Prices & Delivery Times Explained',
    date: '2026-08-15',
    readTime: '5 min read',
    category: 'Shipping',
    author: 'Editorial Team',
    updated: '2026-08-15',
    content: <AllChinaBuyShippingGuide />,
  },
  'allchinabuy-safety-tips': {
    title: 'AllChinaBuy Safety Tips: Protect Yourself When Buying Online',
    date: '2026-08-10',
    readTime: '7 min read',
    category: 'Safety',
    author: 'Editorial Team',
    updated: '2026-08-10',
    content: <AllChinaBuySafetyTips />,
  },
};

function AllChinaBuySpreadsheetGuide() {
  return (
    <>
      <p className="text-lg text-foreground-700 leading-relaxed mb-8">
        Navigating the world of international buying agents can feel overwhelming, especially when you are trying to find the best products at the right prices. The <strong>AllChinaBuy spreadsheet</strong> has emerged as one of the most valuable resources for buyers who want organized, up-to-date product information. In this comprehensive guide, we will walk you through everything you need to know about using the AllChinaBuy spreadsheet effectively in 2026.
      </p>

      <h2 id="what-is" className="text-2xl font-bold text-foreground-950 mt-12 mb-4 scroll-mt-24">
        What Is the AllChinaBuy Spreadsheet?
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        The <a href="/" className="text-primary-700 hover:text-primary-900 underline font-medium">AllChinaBuy spreadsheet</a> is a curated collection of product links, references, and buying information designed to help users navigate the AllChinaBuy platform more efficiently. Unlike browsing directly through the platform, a well-organized spreadsheet gives you a bird&rsquo;s-eye view of available products, categories, and sellers.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-4">
        Think of it as your personal roadmap through the vast landscape of products available through AllChinaBuy. Whether you are looking for shoes, hoodies, accessories, or specialized items, the spreadsheet organizes everything into logical categories that make finding what you need significantly faster.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-8">
        In 2026, the demand for these organized resources has grown exponentially. As more buyers discover the advantages of using buying agents, having a reliable spreadsheet resource has become essential for both beginners and experienced shoppers alike.
      </p>

      <h2 id="why-use" className="text-2xl font-bold text-foreground-950 mt-12 mb-4 scroll-mt-24">
        Why Use an AllChinaBuy Spreadsheet?
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        There are several compelling reasons why using an <strong>AllChinaBuy spreadsheet</strong> can dramatically improve your buying experience:
      </p>
      <div className="bg-background-100 rounded-xl p-6 mb-8">
        <ul className="space-y-3">
          {[
            'Organized product categories that save hours of searching',
            'Direct links to product pages, eliminating navigation confusion',
            'Curated selections from verified sellers',
            'Price comparison capabilities across multiple listings',
            'Regular updates with new product releases',
            'QC photo references for quality verification',
          ].map((item, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-foreground-700">{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <h2 id="how-to-access" className="text-2xl font-bold text-foreground-950 mt-12 mb-4 scroll-mt-24">
        How to Access AllChinaBuy Spreadsheet Resources
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        Finding reliable <a href="/" className="text-primary-700 hover:text-primary-900 underline font-medium">AllChinaBuy spreadsheet links</a> starts with knowing where to look. While there are many unofficial resources floating around the internet, we recommend starting with organized hubs that regularly update their content.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-4">
        The main categories you will typically find in a comprehensive AllChinaBuy spreadsheet include shoes, fashion items, accessories, electronics, and more. Each category is usually broken down into subcategories that make finding specific products even easier.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-8">
        Our <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-700 hover:text-primary-900 underline font-medium">main spreadsheet resource</a> offers direct access to all major categories with regularly updated links and product information. This ensures you are always working with current data rather than outdated references.
      </p>

      <h2 id="categories-explained" className="text-2xl font-bold text-foreground-950 mt-12 mb-4 scroll-mt-24">
        Understanding AllChinaBuy Spreadsheet Categories
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        One of the biggest advantages of using a well-organized spreadsheet is the logical category structure. Here is what you can typically expect:
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {[
          { name: 'Shoes', desc: 'Sneakers, boots, sandals, and casual footwear from various brands' },
          { name: 'Hoodies & Sweaters', desc: 'Streetwear essentials including branded hoodies and knitwear' },
          { name: 'T-Shirts', desc: 'Graphic tees, designer shirts, and casual tops' },
          { name: 'Jackets', desc: 'Outerwear, bomber jackets, puffer coats, and windbreakers' },
          { name: 'Pants & Shorts', desc: 'Jeans, cargo pants, joggers, and athletic shorts' },
          { name: 'Accessories', desc: 'Watches, jewelry, belts, bags, and small items' },
        ].map((cat, idx) => (
          <div key={idx} className="p-4 bg-background-100 rounded-lg border border-background-200/70">
            <h4 className="font-semibold text-foreground-900 mb-1">{cat.name}</h4>
            <p className="text-sm text-foreground-600">{cat.desc}</p>
          </div>
        ))}
      </div>

      <h2 id="using-effectively" className="text-2xl font-bold text-foreground-950 mt-12 mb-4 scroll-mt-24">
        How to Use the Spreadsheet Effectively
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        Simply having access to an <strong>AllChinaBuy spreadsheet</strong> is not enough &mdash; you need to know how to use it effectively. Here are our top recommendations:
      </p>
      <div className="space-y-6 mb-8">
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
          <div>
            <h4 className="font-semibold text-foreground-900 mb-1">Start with a Category</h4>
            <p className="text-foreground-600 text-sm leading-relaxed">Browse by category rather than searching randomly. This helps you discover related products you might not have considered.</p>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
          <div>
            <h4 className="font-semibold text-foreground-900 mb-1">Check Multiple Sellers</h4>
            <p className="text-foreground-600 text-sm leading-relaxed">The same product is often available from different sellers at varying prices. Always compare before deciding.</p>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
          <div>
            <h4 className="font-semibold text-foreground-900 mb-1">Verify QC Photos</h4>
            <p className="text-foreground-600 text-sm leading-relaxed">Quality control photos are your best defense against receiving subpar products. Learn how to read them properly.</p>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center text-sm font-bold flex-shrink-0">4</div>
          <div>
            <h4 className="font-semibold text-foreground-900 mb-1">Read Shipping Information</h4>
            <p className="text-foreground-600 text-sm leading-relaxed">Different shipping methods have different costs and delivery times. Factor this into your total cost calculation.</p>
          </div>
        </div>
      </div>

      <h2 id="safety-first" className="text-2xl font-bold text-foreground-950 mt-12 mb-4 scroll-mt-24">
        Safety First: Protecting Your Purchases
      </h2>
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          Always verify current product, seller, payment, and shipping information before ordering. Details may change over time, and we recommend double-checking all information on the official platform.
        </p>
      </div>
      <p className="text-foreground-700 leading-relaxed mb-4">
        When using any <strong>AllChinaBuy spreadsheet</strong> or buying resource, safety should always be your top priority. The buying agent model involves several steps where things can go wrong if you are not careful.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-8">
        We have created a dedicated <a href="/safety" className="text-primary-700 hover:text-primary-900 underline font-medium">safety guide</a> that covers everything from seller verification to payment protection. We highly recommend reading it before making your first purchase.
      </p>

      <h2 id="faq" className="text-2xl font-bold text-foreground-950 mt-12 mb-4 scroll-mt-24">
        Frequently Asked Questions
      </h2>
      <div className="space-y-4 mb-8">
        {[
          {
            q: 'Is the AllChinaBuy spreadsheet free to use?',
            a: 'Most spreadsheet resources are freely available. However, some premium or specialized spreadsheets may require access through specific communities or platforms.',
          },
          {
            q: 'How often are spreadsheet links updated?',
            a: 'Update frequency varies by resource. Well-maintained spreadsheets are typically updated weekly or monthly. Always check the last update date before relying on links.',
          },
          {
            q: 'Can I trust all sellers in the spreadsheet?',
            a: 'No spreadsheet can guarantee seller quality. We recommend verifying sellers independently through reviews, ratings, and community feedback before placing orders.',
          },
          {
            q: 'What should I do if a link is broken?',
            a: 'Product links can become outdated as sellers update their listings. Try searching for the product name on the platform directly, or look for alternative sellers in the same category.',
          },
          {
            q: 'How do I calculate total costs including shipping?',
            a: 'Total cost = product price + domestic shipping (to warehouse) + international shipping + potential customs fees. Use our shipping guide for detailed calculations.',
          },
        ].map((faq, idx) => (
          <div key={idx} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
            <h4 className="font-semibold text-foreground-900 mb-2">{faq.q}</h4>
            <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>

      <h2 id="conclusion" className="text-2xl font-bold text-foreground-950 mt-12 mb-4 scroll-mt-24">
        Conclusion
      </h2>
      <p className="text-foreground-700 leading-relaxed mb-4">
        The <strong>AllChinaBuy spreadsheet</strong> represents one of the most efficient ways to navigate the vast product selection available through buying agents. By using organized resources, you can save time, compare options more effectively, and ultimately make better purchasing decisions.
      </p>
      <p className="text-foreground-700 leading-relaxed mb-8">
        Remember that no resource is perfect, and the buying landscape changes constantly. Stay informed, prioritize safety, and always verify information before making purchases. For the most current spreadsheet resources, visit our <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-700 hover:text-primary-900 underline font-medium">main website</a> where we maintain regularly updated category links and buying guides.
      </p>
    </>
  );
}

function AllChinaBuyReview() {
  return (
    <>
      <p className="text-lg text-foreground-700 leading-relaxed mb-8">
        This is an independent review of the AllChinaBuy platform. We examine the key features, shipping options, QC processes, and overall user experience to help you make an informed decision.
      </p>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">Platform Overview</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">AllChinaBuy has positioned itself as a competitive buying agent platform. The service offers product sourcing, quality control, warehouse storage, and international shipping.</p>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">Shipping & Costs</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">Shipping costs vary by destination, weight, and chosen carrier. We recommend comparing shipping options before finalizing any purchase.</p>
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-8">
        <p className="text-sm text-amber-800">Always verify current shipping rates and delivery times directly on the platform before ordering.</p>
      </div>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">Conclusion</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">AllChinaBuy offers a solid buying agent experience. Visit our <a href="/" className="text-primary-700 hover:text-primary-900 underline font-medium">spreadsheet resources</a> for more detailed information.</p>
    </>
  );
}

function AllChinaBuyShippingGuide() {
  return (
    <>
      <p className="text-lg text-foreground-700 leading-relaxed mb-8">
        Understanding shipping costs is crucial for budgeting your purchases. This guide breaks down the different shipping methods available through AllChinaBuy.
      </p>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">Shipping Methods</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">AllChinaBuy supports multiple shipping carriers. Each has different price points, delivery speeds, and tracking capabilities.</p>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">Cost Factors</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">Shipping costs depend on package weight, dimensions, destination country, and chosen shipping line.</p>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">Tips for Reducing Costs</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">Consolidating packages, choosing slower shipping lines, and removing unnecessary packaging can all help reduce shipping costs.</p>
    </>
  );
}

function AllChinaBuySafetyTips() {
  return (
    <>
      <p className="text-lg text-foreground-700 leading-relaxed mb-8">
        Staying safe while buying through international agents requires awareness and caution. Follow these essential safety guidelines.
      </p>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">Seller Verification</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">Always check seller ratings, review history, and return policies before placing orders.</p>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">Payment Security</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">Use secure payment methods and never share payment information outside the official platform.</p>
      <h2 className="text-2xl font-bold text-foreground-950 mt-12 mb-4">QC Photo Analysis</h2>
      <p className="text-foreground-700 leading-relaxed mb-8">Learn to identify quality issues in QC photos before approving items for shipment.</p>
    </>
  );
}

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleContent[slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <a href="/" className="text-primary-600 hover:text-primary-800 font-medium">
            {t('article_back')}
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articleList.filter(a => a.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-background-100 border-b border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-500">
              <a href="/" className="hover:text-primary-700 transition-colors">{t('home_breadcrumb')}</a>
              <ChevronRight className="w-4 h-4" />
              <a href="/" className="hover:text-primary-700 transition-colors">{t('article_breadcrumb')}</a>
              <ChevronRight className="w-4 h-4" />
              <span className="text-foreground-800">{article.category}</span>
            </nav>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <header className="pt-8 pb-12 bg-background-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider">
                {article.category}
              </span>
              <span className="flex items-center gap-1 text-xs text-foreground-500">
                <Clock className="w-3.5 h-3.5" />
                {article.readTime}
              </span>
              <span className="flex items-center gap-1 text-xs text-foreground-500">
                <Calendar className="w-3.5 h-3.5" />
                {article.date}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              {article.title}
            </h1>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">{t('article_updated')}: {article.updated}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 rounded-lg bg-background-50 hover:bg-background-200 text-foreground-600 hover:text-foreground-900 transition-colors border border-background-200/70">
                  <Share2 className="w-4 h-4" />
                </button>
                <button className="p-2 rounded-lg bg-background-50 hover:bg-background-200 text-foreground-600 hover:text-foreground-900 transition-colors border border-background-200/70">
                  <Bookmark className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-12">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            {/* Table of Contents */}
            <div className="mb-10 p-6 bg-background-100 rounded-xl border border-background-200/70">
              <h3 className="font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                <Star className="w-4 h-4 text-primary-600" />
                {t('article_table_of_contents')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {slug === 'allchinabuy-spreadsheet-complete-guide' && [
                  { id: 'what-is', label: 'What Is the AllChinaBuy Spreadsheet?' },
                  { id: 'why-use', label: 'Why Use an AllChinaBuy Spreadsheet?' },
                  { id: 'how-to-access', label: 'How to Access Spreadsheet Resources' },
                  { id: 'categories-explained', label: 'Understanding Spreadsheet Categories' },
                  { id: 'using-effectively', label: 'How to Use the Spreadsheet Effectively' },
                  { id: 'safety-first', label: 'Safety First' },
                  { id: 'faq', label: 'Frequently Asked Questions' },
                  { id: 'conclusion', label: 'Conclusion' },
                ].map((item, idx) => (
                  <a
                    key={idx}
                    href={`#${item.id}`}
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-700 transition-colors py-1"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-primary-400" />
                    {item.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="prose prose-lg max-w-none">
              {article.content}
            </div>

            {/* CTA Box */}
            <div className="mt-12 p-8 bg-primary-50 rounded-xl border border-primary-200/70">
              <h3 className="text-xl font-bold text-foreground-950 mb-3">
                Ready to Explore AllChinaBuy?
              </h3>
              <p className="text-foreground-700 mb-6">
                Visit our main spreadsheet resource to browse categories, discover products, and start your buying journey with confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-3 text-sm font-semibold text-background-50 bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {t('cta_spreadsheet')}
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a
                  href="/"
                  className="px-6 py-3 text-sm font-semibold text-foreground-700 bg-background-50 hover:bg-background-100 border border-background-200 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {t('article_back')}
                  <ArrowLeft className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-12 bg-background-100 border-t border-background-200/70">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-foreground-950 mb-6">{t('article_related')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra, idx) => (
                <a
                  key={idx}
                  href={`/article/${ra.slug}`}
                  className="group p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300/50 transition-all hover:shadow-md"
                >
                  <span className="px-2.5 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold mb-3 inline-block">
                    {ra.category}
                  </span>
                  <h4 className="font-semibold text-foreground-900 mb-2 group-hover:text-primary-700 transition-colors text-sm">
                    {ra.title}
                  </h4>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                    {t('article_read_more')}
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category Links */}
      <section className="py-12 bg-background-50">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-foreground-950 mb-6">Explore Categories</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {categories.slice(0, 8).map((cat, idx) => (
                <a
                  key={idx}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 p-3 bg-background-100 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-200 transition-colors border border-background-200/70"
                >
                  <ChevronRight className="w-3.5 h-3.5 text-primary-500" />
                  {cat.name}
                  <ExternalLink className="w-3 h-3 text-foreground-400 ml-auto" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}